import { z } from "zod";

export const SessionRunStateSchema = z.enum([
  "queued",
  "dispatched",
  "leased",
  "retry",
  "local_run",
  "ready",
  "failed",
  "timed_out",
  "cancelled",
  "superseded",
  "succeeded",
  // FE-only sentinel — never sent by the backend. A state value this build
  // doesn't know (a newer backend mid-rollout) is coerced here by the
  // `.catch` on `SessionRunSchema.state`, so one new state degrades that row
  // instead of failing the whole array parse and freezing the panel
  // (MS-3366). Deliberately excluded from the grid's ACTIVE/ERROR state sets:
  // an unknown-state row renders as settled, never as a permanent spinner.
  "unknown",
]);

export const SessionRunSchema = z.object({
  id: z.string().uuid(),
  sessionId: z.string(),
  sourceRowId: z.string(),
  state: SessionRunStateSchema.catch("unknown"),
  // The engine that owns the row. "analytics" is a Generate's analytics
  // workflow row, carrying one sample's analytics columns beside the worker's
  // row for the same sample. Absent on rows the browser builds for itself;
  // nullish so a null reads as "not analytics" instead of dropping the row.
  engine: z.string().nullish(),
  checkpointId: z.string().nullable(),
  targetColumnIds: z.array(z.string()).nullable(),
  workerId: z.string().nullable(),
  eventRunId: z.string().uuid().nullable(),
  leasedAt: z.string().or(z.date()).nullable(),
  leaseExpiresAt: z.string().or(z.date()).nullable(),
  leaseToken: z.number().int(),
  cancelled: z.boolean(),
  attempt: z.number().int(),
  createdAt: z.string().or(z.date()),
  updatedAt: z.string().or(z.date()),
  failureReason: z.string().nullable(),
  columnErrors: z.record(z.string()).nullable().optional(),
});

export type SessionRunState = z.infer<typeof SessionRunStateSchema>;
export type SessionRun = z.infer<typeof SessionRunSchema>;
